import React, { useEffect, useState } from "react";
import GlobalDialog from "../../../../components/core/global-dialog";
import GlobalButton from "../../../../components/core/global-button";
import GlobalTextArea from "../../../../components/core/global-text-area";

const errorImage = `${process.env.PUBLIC_URL}/assets/images/error.png`;
const successImage = `${process.env.PUBLIC_URL}/assets/images/check.png`;
const warningImage = `${process.env.PUBLIC_URL}/assets/images/warning.png`;

const VARIANTS = {
  reject: {
    icon: errorImage,
    iconClass: "text-red-600 bg-red-50 border border-red-200",
    confirmClass:
      "bg-red-600 hover:bg-red-700 text-white border border-red-600",
    focusClass: "focus:ring-red-400 focus:border-red-400",
    confirmLabel: "Reject",
  },
  accept: {
    icon: successImage,
    iconClass: "text-green-700 bg-green-50 border border-green-200",
    confirmClass:
      "bg-green-700 hover:bg-green-800 text-white border border-green-700",
    focusClass: "focus:ring-green-400 focus:border-green-400",
    confirmLabel: "Accept",
  },
  verify: {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        width={20}
        height={20}
      >
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ),
    iconClass: "text-blue-600 bg-blue-50 border border-blue-200",
    confirmClass: "bg-blue-600 hover:bg-blue-700 text-white",
    focusClass: "focus:ring-blue-400 focus:border-blue-400",
    confirmLabel: "Verify",
  },
  warning: {
    icon: warningImage,
    iconClass: "text-orange-600 bg-orange-50 border border-orange-200",
    confirmClass: "bg-orange-600 hover:bg-orange-700 text-white",
    focusClass: "focus:ring-orange-400 focus:border-orange-400",
    confirmLabel: "Confirm",
  },
};

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  variant = "warning",
  title = "Are you sure?",
  subtitle = "This action cannot be undone.",
  messageLabel = "Message",
  messagePlaceholder = "Add a message...",
  defaultMessage = "",
  messageReadOnly = false,
  isAgentAction = false,
  cancelLabel = "Cancel",
  confirmLabel,
  defaultFallbackMessage,
  defaultFallbackPrivateNote = "",
  showPrivateNote = false,
  requireMessage = false,
  isSaving = false,
}) => {
  const [message, setMessage] = useState(defaultMessage);
  const [privateNote, setPrivateNote] = useState("");
  const cfg = VARIANTS[variant] || VARIANTS.warning;
  const resolvedConfirmLabel = confirmLabel || cfg.confirmLabel;

  const getSMSParts = (length) => {
    if (length === 0) return 0;
    if (length <= 160) return 1;
    return Math.ceil(length / 153);
  };
  const smsParts = getSMSParts(message.length);

  useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage]);

  const handleConfirm = async () => {
    const finalMessage = message.trim()
      ? `${defaultFallbackMessage}\n\n${message.trim()}`
      : defaultFallbackMessage || "";

    const finalPrivateNote = privateNote.trim()
      ? `${defaultFallbackPrivateNote}\n\n${privateNote.trim()}`
      : defaultFallbackPrivateNote || "";

    await onConfirm?.({ message: finalMessage, privateNote: finalPrivateNote });
    onClose?.();
  };

  const handleClose = () => {
    setMessage(defaultMessage);
    setPrivateNote("");
    onClose?.();
  };

  return (
    <GlobalDialog
      visible={open}
      onHide={handleClose}
      dialogClassName="min-w-[420px] max-w-[560px]"
      footer={
        <div className="flex justify-center gap-2">
          <GlobalButton
            label={cancelLabel}
            onClick={handleClose}
            parentClassName={`w-full`}
            buttonClassName="w-full text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
          />
          <GlobalButton
            label={resolvedConfirmLabel}
            onClick={handleConfirm}
            disabled={isSaving}
            parentClassName={`w-full`}
            buttonClassName={`w-full text-sm font-semibold rounded-lg transition-colors active:scale-95 ${cfg.confirmClass}`}
          />
        </div>
      }
    >
      <div className="flex flex-col gap-5 p-2 font-montserrat text-secondary-purpleGray">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div
            className={`flex items-center justify-center w-11 h-11 rounded-xl shrink-0 ${cfg.iconClass}`}
          >
            {typeof cfg.icon === "string" ? (
              <img
                src={cfg.icon}
                alt={variant}
                className="w-6 h-6 object-contain"
              />
            ) : (
              cfg.icon
            )}
          </div>
          <div className="flex flex-col gap-1 pt-0.5">
            <h3 className="text-base font-semibold text-gray-800 leading-snug m-0">
              {title}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed m-0">
              {subtitle}
            </p>
          </div>
        </div>

        {/* Agent badge */}
        {isAgentAction && (
          <span className="inline-flex items-center gap-1.5 self-start text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-3 py-1">
            <svg viewBox="0 0 16 16" fill="currentColor" width={11} height={11}>
              <path d="M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1zm0 2a5 5 0 1 0 0 10A5 5 0 0 0 8 3zm.75 2v3.44l2.25 1.3-.75 1.3L7.25 9.5V5h1.5z" />
            </svg>
            Agent Action
          </span>
        )}

        {/* Message to user */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1">
            {messageLabel}
            {requireMessage && <span className="text-red-500">*</span>}
          </label>
          {messageReadOnly ? (
            <div className="w-full px-3 py-2.5 text-sm leading-relaxed bg-gray-50 border border-gray-200 rounded-lg text-gray-700 min-h-[60px] break-words whitespace-pre-wrap">
              {message || (
                <span className="text-gray-400 italic">
                  No message provided.
                </span>
              )}
            </div>
          ) : (
            <>
              <GlobalTextArea
                inputClassName={`w-full px-3 py-2.5 text-sm leading-relaxed border rounded-lg bg-white text-gray-700 resize-none outline-none transition-colors focus:ring-1 focus:ring-offset-0 border-gray-200 ${cfg.focusClass}`}
                placeholder={messagePlaceholder}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
              <div className="flex justify-end items-center gap-1.5 text-xs text-gray-400 mt-1">
                <span>{message.length} chars</span>
                <span>·</span>
                <span
                  className={smsParts > 1 ? "text-orange-500 font-medium" : ""}
                >
                  {smsParts <= 1
                    ? `${Math.max(smsParts, 0)} SMS part`
                    : `${smsParts} SMS parts`}
                </span>
              </div>
              {defaultFallbackMessage && (
                <p className="text-xs text-gray-500 flex items-start gap-1 bg-slate-100 rounded-lg p-2">
                  <span className="mt-0.5">💡</span>
                  <span>
                    If left blank, the default message{" "}
                    <span className="italic text-gray-700">
                      "{defaultFallbackMessage}"
                    </span>{" "}
                    will be sent instead.
                  </span>
                </p>
              )}
            </>
          )}
        </div>

        {/* Private note */}
        {showPrivateNote && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide">
              Private Comments/Reason
            </label>
            <p className="text-xs text-gray-400 -mt-1">
              Not visible to the user.
            </p>
            <GlobalTextArea
              inputClassName={`w-full px-3 py-2.5 text-sm leading-relaxed border border-gray-200 rounded-lg bg-white text-gray-700 resize-none outline-none transition-colors focus:ring-1 focus:ring-offset-0 ${cfg.focusClass}`}
              placeholder={
                defaultFallbackPrivateNote
                  ? `Defaults to "${defaultFallbackPrivateNote}" if left blank`
                  : "Add internal notes (not visible to user)..."
              }
              value={privateNote}
              maxLength={1000}
              onChange={(e) => setPrivateNote(e.target.value)}
            />
            <div className="flex justify-end items-center text-xs text-gray-400">
              <span>{privateNote.length} / 1000</span>
            </div>
            {defaultFallbackPrivateNote && (
              <p className="text-xs text-gray-500 flex items-start gap-1 bg-slate-100 rounded-lg p-2">
                <span className="mt-0.5">💡</span>
                <span>
                  If left blank, the default message{" "}
                  <span className="italic text-gray-700">
                    "{defaultFallbackPrivateNote}"
                  </span>{" "}
                  will be included in the private comments.
                </span>
              </p>
            )}
          </div>
        )}
      </div>
    </GlobalDialog>
  );
};

export default ConfirmationDialog;
